import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  Tooltip,
  Avatar,
  Stack,
  Grid,
  Popover,
  InputBase,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventIcon from '@mui/icons-material/Event';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import NumbersIcon from '@mui/icons-material/Numbers';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useReportManagement } from '../../../hooks/useReportManagement';
import { useAuth, useNotification, useReportManagementPermissions } from '../../../hooks';
import ReportFlowItemActivityFeed from '../../../components/ActivityFeed';

// ─── FlowTagPicker ──────────────────────────────────────────────────────
// Notion/Airtable tarzi etiket seçici: yüzeyde renkli pill'ler + "+ Ekle"
// chip; tıklayınca Popover açılır, arama input'lu tag listesi. Her item
// hover'da vurgulanir, tikladiginda toggle olur ve backend'e anlik yansir
// (optimistic). Tag'e X ile kaldirma. Dısarı tıklamak Popover'ı kapatir.
const FlowTagPicker = ({ flowTags, itemTags, onSave, disabled }) => {
  const initialIds = React.useMemo(
    () => (itemTags || []).map((t) => t.id),
    [itemTags],
  );
  const [selectedIds, setSelectedIds] = useState(initialIds);
  const [saving, setSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');
  const savingRef = React.useRef(false);
  const pendingRef = React.useRef(null);

  useEffect(() => {
    setSelectedIds((itemTags || []).map((t) => t.id));
  }, [itemTags]);

  // Optimistic + serialize save. Ust uste tiklamalarda son degeri gecerli
  // kabul edip pending kuyrukla yalniz son'u backend'e gonderir.
  const persist = async (nextIds) => {
    setSelectedIds(nextIds);
    if (savingRef.current) {
      pendingRef.current = nextIds;
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      let toSend = nextIds;
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        await onSave(toSend);
        if (pendingRef.current == null) break;
        toSend = pendingRef.current;
        pendingRef.current = null;
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const toggle = (id) => {
    if (disabled) return;
    persist(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  };

  const remove = (id) => (e) => {
    e.stopPropagation();
    if (disabled) return;
    persist(selectedIds.filter((x) => x !== id));
  };

  const openPicker = (e) => { if (!disabled) setAnchorEl(e.currentTarget); };
  const closePicker = () => { setAnchorEl(null); setSearch(''); };

  const assigned = React.useMemo(
    () => selectedIds.map((id) => flowTags.find((t) => t.id === id)).filter(Boolean),
    [flowTags, selectedIds],
  );

  const filteredTags = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flowTags;
    return flowTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [flowTags, search]);

  const pickerOpen = Boolean(anchorEl);

  return (
    <Box
      sx={{
        mt: 1.5, p: 1.5, borderRadius: '12px',
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <Box
          sx={{
            width: 26, height: 26, borderRadius: '8px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          <LocalOfferOutlinedIcon sx={{ fontSize: '0.95rem' }} />
        </Box>
        <Typography
          sx={{
            fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1,
          }}
        >
          Etiketler {assigned.length > 0 && (
            <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
              · {assigned.length}
            </Box>
          )}
        </Typography>
        {saving && <CircularProgress size={12} sx={{ color: 'text.disabled' }} />}
      </Box>

      {/* Pill'ler + "+ Ekle" chip */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {assigned.map((tag) => (
          <Box
            key={tag.id}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.35,
              pl: 0.85, pr: disabled ? 0.85 : 0.35, py: 0.3,
              borderRadius: '999px',
              bgcolor: tag.color, color: '#fff',
              fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.4,
              boxShadow: `0 1px 2px ${alpha(tag.color, 0.4)}`,
              transition: 'transform 0.1s',
              '&:hover': !disabled ? { transform: 'translateY(-1px)' } : undefined,
            }}
          >
            {tag.name}
            {!disabled && (
              <Box
                component="button"
                type="button"
                onClick={remove(tag.id)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 16, height: 16, borderRadius: '50%',
                  border: 'none', p: 0, cursor: 'pointer',
                  bgcolor: 'rgba(0,0,0,0.18)', color: '#fff',
                  opacity: 0.9, transition: 'background-color 0.12s, opacity 0.12s',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.35)', opacity: 1 },
                }}
              >
                <CloseIcon sx={{ fontSize: 10 }} />
              </Box>
            )}
          </Box>
        ))}

        {!disabled && (
          <Box
            component="button"
            type="button"
            onClick={openPicker}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.35,
              px: 0.85, py: 0.3, borderRadius: '999px',
              border: '1.5px dashed',
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
              bgcolor: 'transparent', color: 'primary.main',
              fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.4,
              cursor: 'pointer',
              transition: 'background-color 0.12s, border-color 0.12s',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderColor: 'primary.main',
              },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            {assigned.length === 0 ? 'Etiket Ekle' : 'Ekle'}
          </Box>
        )}
      </Box>

      {/* Popover — arama + tag listesi */}
      <Popover
        open={pickerOpen}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5, width: 300, maxWidth: '90vw',
              borderRadius: '12px', overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(15,23,42,0.15)',
              border: '1px solid #e2e8f0',
            },
          },
        }}
      >
        {/* Arama */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9',
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <InputBase
            autoFocus
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, fontSize: '0.85rem' }}
          />
          {saving && <CircularProgress size={12} sx={{ color: 'text.disabled' }} />}
        </Box>

        {/* Tag listesi */}
        <Box sx={{ maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
          {flowTags.length === 0 ? (
            <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.5 }}>
                Bu akış için etiket yok.
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                Akış başlığındaki 🏷 butonundan ekleyin.
              </Typography>
            </Box>
          ) : filteredTags.length === 0 ? (
            <Typography sx={{ px: 1.5, py: 2, fontSize: '0.78rem', color: 'text.disabled', textAlign: 'center' }}>
              "{search}" için sonuç yok.
            </Typography>
          ) : (
            filteredTags.map((tag) => {
              const isSel = selectedIds.includes(tag.id);
              return (
                <Box
                  key={tag.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(tag.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(tag.id); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 0.85, cursor: 'pointer',
                    transition: 'background-color 0.1s',
                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) },
                    outline: 'none',
                    '&:focus-visible': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) },
                  }}
                >
                  {/* Renkli pill preview */}
                  <Box
                    sx={{
                      display: 'inline-flex', alignItems: 'center',
                      px: 0.85, py: 0.25, borderRadius: '999px',
                      bgcolor: tag.color, color: '#fff',
                      fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.3,
                      maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {tag.name}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  {isSel ? (
                    <Box
                      sx={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 20, height: 20, borderRadius: '50%',
                        bgcolor: tag.color, color: '#fff', flexShrink: 0,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 12 }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: '1.5px solid', borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </Box>
  );
};

const JOB_TYPE_COLORS = {
  AR_GE: '#7c3aed',
  TASARIM_MERKEZI: '#4338ca',
  TEKNOPARK: '#0d9488',
  TUBITAK: '#d97706',
  ULUSLARARASI: '#0284c7',
};

const STATUS_COLOR_MAP = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  OVERDUE: 'error',
};

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#10b981', '#0ea5e9', '#f59e0b', '#ef4444'];

function fmtDate(s) {
  if (!s) return '-';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function relativeTime(s, t) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (sec < 30) return t('reportManagement.cardDetail.justNow', { defaultValue: 'şimdi' });
  if (min < 1) return t('reportManagement.cardDetail.secondsAgo', { defaultValue: '{{n}} saniye önce', n: sec });
  if (hr < 1) return t('reportManagement.cardDetail.minutesAgo', { defaultValue: '{{n}} dakika önce', n: min });
  if (day < 1) return t('reportManagement.cardDetail.hoursAgo', { defaultValue: '{{n}} saat önce', n: hr });
  if (day < 7) return t('reportManagement.cardDetail.daysAgo', { defaultValue: '{{n}} gün önce', n: day });
  return fmtDate(s);
}

function getReport(item) {
  const r = item?.job?.job_reports;
  if (!Array.isArray(r) || !item?.report_id) return null;
  return r.find((x) => String(x.id) === String(item.report_id)) || null;
}

function userInitials(u) {
  const f = (u?.first_name || '').trim();
  const l = (u?.last_name || '').trim();
  return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?';
}

function userFullName(u) {
  if (!u) return '-';
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || '-';
}

function colorForUser(u) {
  if (!u) return AVATAR_COLORS[0];
  const id = String(u.id || u.first_name || '?');
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

// ─── InfoCard ─────────────────────────────────────────────────────────────────

function InfoCard({ icon, label, children, accentColor }) {
  const theme = useTheme();
  const accent = accentColor || theme.palette.primary.main;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: alpha(accent, 0.15),
        bgcolor: alpha(accent, 0.04),
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        height: '100%',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
          borderColor: alpha(accent, 0.3),
          bgcolor: alpha(accent, 0.06),
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          bgcolor: alpha(accent, 0.14),
          color: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          '& svg': { fontSize: '1.05rem' },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: '0.68rem',
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            mb: 0.25,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ fontSize: '0.85rem', color: 'text.primary', fontWeight: 600, lineHeight: 1.35 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

// ─── CommentItem ──────────────────────────────────────────────────────────────

function CommentItem({ comment, canDeleteThis, onDelete, t }) {
  const theme = useTheme();
  const avatarColor = colorForUser(comment.user);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        p: 1.5,
        borderRadius: '10px',
        bgcolor: alpha(theme.palette.grey[500], 0.04),
        border: '1px solid',
        borderColor: alpha(theme.palette.grey[500], 0.12),
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: alpha(theme.palette.grey[500], 0.06),
        },
      }}
    >
      <Avatar
        sx={{
          width: 34,
          height: 34,
          bgcolor: avatarColor,
          fontSize: '0.78rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {userInitials(comment.user)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>
            {userFullName(comment.user)}
          </Typography>
          <Tooltip title={new Date(comment.created_at).toLocaleString('tr-TR')}>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              {relativeTime(comment.created_at, t)}
            </Typography>
          </Tooltip>
          {comment.updated_at && comment.updated_at !== comment.created_at && (
            <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontStyle: 'italic' }}>
              ({t('reportManagement.cardDetail.edited', { defaultValue: 'düzenlendi' })})
            </Typography>
          )}
          {canDeleteThis && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
              <Tooltip title={t('reportManagement.cardDetail.deleteComment', { defaultValue: 'Sil' })}>
                <IconButton size="small" onClick={() => onDelete(comment)} sx={{ p: 0.5 }}>
                  <DeleteOutlineIcon sx={{ fontSize: '0.95rem', color: theme.palette.error.main }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: '0.85rem',
            color: 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.5,
          }}
        >
          {comment.content}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── CardDetailModal ──────────────────────────────────────────────────────────

const CardDetailModal = ({
  open, item, onClose, onCommentChange,
  // Akis-ozel etiket props (opsiyonel — degilse tag bolumu gizli).
  flowTags = [], itemTags = [], onSaveTags = null,
  // Rapor turu id -> ad map (parent'tan). job_reports JSONB'de reportType
  // ID olarak saklanan eski kayitlar icin lookup — aksi halde "1" gorunuyor.
  reportTypeMap = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const permissions = useReportManagementPermissions();
  const {
    fetchItemComments,
    addItemComment,
    deleteItemComment,
  } = useReportManagement();

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isRoot = user?.role === 'ROOT';
  const canCreate = permissions?.canCreate || isRoot;
  const canDelete = permissions?.canDelete || isRoot;

  // ── Derived view-model ─────────────────────────────────────────────────────
  const job = item?.job || {};
  const report = useMemo(() => getReport(item), [item]);
  const firmName = job?.contract?.firm?.firm_name || job?.firm_short_name || '-';
  const jobTitle = job?.job_title || '-';
  const projectNumber = job?.project_number || null;
  const jobType = job?.job_type || null;
  const jobTypeColor = JOB_TYPE_COLORS[jobType] || theme.palette.grey[500];
  // Rapor turu ismi: once explicit `reportTypeName` (yeni kayitlar), sonra
  // parent'tan gelen id->name map lookup, en son ham deger. Bu sekilde eski
  // job_reports kayitlarinda "1" gibi ham ID goruntulenmez.
  const reportTypeRaw = report?.reportType;
  const reportTypeName = report?.reportTypeName
    || (reportTypeMap && reportTypeRaw != null ? reportTypeMap[String(reportTypeRaw)] : null)
    || (typeof reportTypeRaw === 'string' ? reportTypeRaw : null);
  const tags = Array.isArray(report?.reportTags) ? report.reportTags : [];
  const status = report?.status || null;
  const statusColorKey = STATUS_COLOR_MAP[status] || 'default';
  const statusPaletteColor = theme.palette[statusColorKey]?.main || theme.palette.grey[500];
  const technical = job?.job_assignment?.technical_user;
  const financial = job?.job_assignment?.financial_user;

  // ── TUBITAK proje detay tarihleri ────────────────────────────────────────
  const isTubitak = jobType === 'TUBITAK' || (Array.isArray(job?.job_details) && job.job_details.length > 0);
  const jobDetailData = Array.isArray(job?.job_details) ? job.job_details[0]?.detail_data : null;
  const projectStartDate = jobDetailData?.projectStartDate || null;
  const projectEndDate = jobDetailData?.projectEndDate || null;
  const contractSigningDate = job?.contract?.contract_signing_date || null;
  // Fallback: iş durumu ONAYLANDI değilse contract.updated_at'i kullan
  const approvalDate = job?.job_status === 'ONAYLANDI'
    ? (job?.updated_at || null)
    : (job?.contract?.updated_at || null);
  const hasAnyProjectDetail = !!(projectStartDate || projectEndDate || contractSigningDate || approvalDate);

  // ── Load comments when item changes ───────────────────────────────────────
  const loadComments = useCallback(async () => {
    if (!item?.id) return;
    setLoadingComments(true);
    try {
      const list = await fetchItemComments(item.id);
      setComments(list);
    } finally {
      setLoadingComments(false);
    }
  }, [item?.id, fetchItemComments]);

  useEffect(() => {
    if (open && item?.id) {
      loadComments();
      setNewComment('');
    }
  }, [open, item?.id, loadComments]);

  // ── Add comment ────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    const body = newComment.trim();
    if (!body || !item?.id) return;
    setSubmitting(true);
    try {
      const created = await addItemComment(item.id, body);
      if (created) {
        setComments((prev) => [...prev, created]);
        setNewComment('');
        showNotification(
          t('reportManagement.cardDetail.commentSaved', { defaultValue: 'Yorum kaydedildi' }),
          'success'
        );
        if (onCommentChange) onCommentChange();
      }
    } catch (_err) {
      showNotification(
        t('reportManagement.cardDetail.commentError', { defaultValue: 'Yorum işlenirken hata oluştu' }),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (comment) => {
    if (!comment?.id) return;
    const ok = window.confirm(
      t('reportManagement.cardDetail.confirmDelete', { defaultValue: 'Yorumu silmek istediğinize emin misiniz?' })
    );
    if (!ok) return;
    try {
      await deleteItemComment(comment.id);
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
      showNotification(
        t('reportManagement.cardDetail.commentDeleted', { defaultValue: 'Yorum silindi' }),
        'success'
      );
      if (onCommentChange) onCommentChange();
    } catch (_err) {
      showNotification(
        t('reportManagement.cardDetail.commentError', { defaultValue: 'Yorum işlenirken hata oluştu' }),
        'error'
      );
    }
  };

  const isOwnComment = (comment) =>
    user?.id && String(user.id) === String(comment.user_id);

  // Author always can delete own; ROOT always can; otherwise need canDelete.
  const canDeleteComment = (comment) =>
    isRoot || isOwnComment(comment) || canDelete;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', maxHeight: '92vh' } }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          p: 0,
          background: `linear-gradient(135deg, ${alpha(jobTypeColor, 0.08)} 0%, ${alpha(jobTypeColor, 0.02)} 100%)`,
          borderBottom: '1px solid',
          borderColor: alpha(jobTypeColor, 0.12),
        }}
      >
        <Box sx={{ p: 2.5, pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: alpha(jobTypeColor, 0.18),
                color: jobTypeColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <BusinessIcon sx={{ fontSize: '1.4rem' }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.15rem',
                  color: 'text.primary',
                  lineHeight: 1.25,
                  mb: 0.25,
                }}
              >
                {firmName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.88rem',
                  color: 'text.secondary',
                  fontWeight: 500,
                  lineHeight: 1.35,
                }}
              >
                {jobTitle}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {projectNumber && (
                  <Chip
                    size="small"
                    icon={<NumbersIcon sx={{ fontSize: '0.85rem !important' }} />}
                    label={projectNumber}
                    sx={{
                      height: 24,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.grey[600], 0.1),
                      color: 'text.secondary',
                      borderRadius: '8px',
                      '& .MuiChip-icon': { color: 'text.secondary', ml: 0.5 },
                    }}
                  />
                )}
                {jobType && (
                  <Chip
                    size="small"
                    label={t(`jobs.jobTypes.${jobType}`, { defaultValue: jobType })}
                    sx={{
                      height: 24,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      bgcolor: alpha(jobTypeColor, 0.14),
                      color: jobTypeColor,
                      borderRadius: '8px',
                    }}
                  />
                )}
                {status && (
                  <Chip
                    size="small"
                    label={t(`reportManagement.statuses.${status}`, { defaultValue: status })}
                    sx={{
                      height: 24,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      bgcolor: alpha(statusPaletteColor, 0.15),
                      color: statusPaletteColor,
                      borderRadius: '8px',
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
          size="small"
        >
          <CloseIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: alpha(theme.palette.grey[500], 0.025) }}>
        {/* ── Bilgiler ─────────────────────────────────────────────────── */}
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 800,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {t('reportManagement.cardDetail.info', { defaultValue: 'Bilgiler' })}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>

          <Grid container spacing={1.25}>
            {reportTypeName && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InfoCard
                  icon={<AssignmentOutlinedIcon />}
                  label={t('reportManagement.table.reportType', { defaultValue: 'Rapor Türü' })}
                  accentColor={theme.palette.primary.main}
                >
                  {reportTypeName}
                </InfoCard>
              </Grid>
            )}

            {jobType && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InfoCard
                  icon={<WorkOutlineIcon />}
                  label={t('reportManagement.table.jobType', { defaultValue: 'İş Türü' })}
                  accentColor={jobTypeColor}
                >
                  {t(`jobs.jobTypes.${jobType}`, { defaultValue: jobType })}
                </InfoCard>
              </Grid>
            )}

            {(report?.startDate || report?.endDate) && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InfoCard
                  icon={<DateRangeIcon />}
                  label={t('reportManagement.cardDetail.dateRange', { defaultValue: 'Tarih Aralığı' })}
                  accentColor={theme.palette.info.main}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {fmtDate(report?.startDate)}
                    </Typography>
                    <Typography component="span" sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>→</Typography>
                    <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {fmtDate(report?.endDate)}
                    </Typography>
                  </Box>
                </InfoCard>
              </Grid>
            )}

            {(technical || financial) && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InfoCard
                  icon={<PersonOutlineIcon />}
                  label={t('reportManagement.cardDetail.responsibles', { defaultValue: 'Sorumlular' })}
                  accentColor={theme.palette.secondary.main}
                >
                  <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                    {technical && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, minWidth: 48 }}>
                          {t('reportManagement.cardDetail.technicalShort', { defaultValue: 'Tek.' })}
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                          {userFullName(technical)}
                        </Typography>
                      </Box>
                    )}
                    {financial && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, minWidth: 48 }}>
                          {t('reportManagement.cardDetail.financialShort', { defaultValue: 'Mali' })}
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                          {userFullName(financial)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </InfoCard>
              </Grid>
            )}

            {item?.completion_date && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InfoCard
                  icon={<EventAvailableIcon />}
                  label={t('reportManagement.card.completedOn', { defaultValue: 'Tamamlandı' })}
                  accentColor={theme.palette.success.main}
                >
                  {fmtDate(item.completion_date)}
                </InfoCard>
              </Grid>
            )}

            {item?.submission_date && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <InfoCard
                  icon={<EventIcon />}
                  label={t('reportManagement.card.submittedOn', { defaultValue: 'Gönderildi' })}
                  accentColor={theme.palette.success.main}
                >
                  {fmtDate(item.submission_date)}
                </InfoCard>
              </Grid>
            )}
          </Grid>

          {tags.length > 0 && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: alpha(theme.palette.grey[500], 0.15),
                bgcolor: alpha(theme.palette.grey[500], 0.04),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                <LabelOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    color: 'text.secondary',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('reportManagement.table.tags', { defaultValue: 'Etiketler' })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    size="small"
                    label={tag}
                    sx={{
                      bgcolor: 'background.paper',
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      height: 22,
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: 'divider',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Akis-ozel etiketler: bu akista tanimlanmis etiketleri karta
              ekle/cikar. YETKI: akistaki karti gorebilen herkes etiket
              ekleyebilsin — kullanici istegi: "herkes gorebildigi
              akistaki raporlara benim kaydettigim etiketleri ekleyebilsin".
              Onceden `disabled={!canCreate}` sadece Rapor Yonetimi > yaratma
              yetkisi olanlara aciyordu (Hasura permission tarafinda ise
              team_member dahil hepsine INSERT yetkisi var). Modal zaten
              yalniz gorebilenlere aciliyor, ek kisitlamaya gerek yok. */}
          {onSaveTags && (
            <FlowTagPicker
              flowTags={flowTags}
              itemTags={itemTags}
              onSave={onSaveTags}
              disabled={false}
            />
          )}
        </Box>

        {isTubitak && hasAnyProjectDetail && (
          <>
            <Divider />
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {t('reportManagement.detail.projectDetails', { defaultValue: 'Proje Detayları' })}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>

              <Grid container spacing={1.25}>
                {projectStartDate && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <InfoCard
                      icon={<EventIcon />}
                      label={t('reportManagement.detail.projectStartDate', { defaultValue: 'Proje Başlangıç Tarihi' })}
                      accentColor={theme.palette.info.main}
                    >
                      {fmtDate(projectStartDate)}
                    </InfoCard>
                  </Grid>
                )}
                {projectEndDate && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <InfoCard
                      icon={<EventIcon />}
                      label={t('reportManagement.detail.projectEndDate', { defaultValue: 'Proje Bitiş Tarihi' })}
                      accentColor={theme.palette.warning.main}
                    >
                      {fmtDate(projectEndDate)}
                    </InfoCard>
                  </Grid>
                )}
                {contractSigningDate && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <InfoCard
                      icon={<DateRangeIcon />}
                      label={t('reportManagement.detail.contractSigningDate', { defaultValue: 'Sözleşme Tarihi' })}
                      accentColor={theme.palette.secondary.main}
                    >
                      {fmtDate(contractSigningDate)}
                    </InfoCard>
                  </Grid>
                )}
                {approvalDate && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <InfoCard
                      icon={<EventAvailableIcon />}
                      label={t('reportManagement.detail.approvalDate', { defaultValue: 'Onaylanma Tarihi' })}
                      accentColor={theme.palette.success.main}
                    >
                      {fmtDate(approvalDate)}
                    </InfoCard>
                  </Grid>
                )}
              </Grid>
            </Box>
          </>
        )}

        <Divider />

        {/* ── Aktivite gecmisi (sistem uretir; kutucuk sutun degistirilince
             veya arsivlenince trigger otomatik row atar). ────────────────── */}
        <Box sx={{ p: 2.5, bgcolor: 'background.paper' }}>
          <ReportFlowItemActivityFeed itemId={item?.id} />
        </Box>

        <Divider />

        {/* ── Yorumlar ───────────────────────────────────────────────────── */}
        <Box sx={{ p: 2.5, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: '1.05rem', color: 'text.secondary' }} />
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 800,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {t('reportManagement.cardDetail.comments', { defaultValue: 'Yorumlar' })}
            </Typography>
            {comments.length > 0 && (
              <Chip
                size="small"
                label={comments.length}
                sx={{
                  height: 18,
                  minWidth: 22,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          </Box>

          {loadingComments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} sx={{ color: 'text.secondary' }} />
            </Box>
          ) : (
            <>
              {comments.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    px: 2,
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.grey[500], 0.03),
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                  }}
                >
                  <ChatBubbleOutlineIcon sx={{ fontSize: '1.75rem', color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', fontWeight: 500 }}>
                    {t('reportManagement.cardDetail.noComments', { defaultValue: 'Henüz yorum yok' })}
                  </Typography>
                  {canCreate && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                      {t('reportManagement.cardDetail.beFirst', { defaultValue: 'İlk yorumu siz yazın' })}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Stack spacing={1}>
                  {comments.map((c) => (
                    <CommentItem
                      key={c.id}
                      comment={c}
                      canDeleteThis={canDeleteComment(c)}
                      onDelete={handleDelete}
                      t={t}
                    />
                  ))}
                </Stack>
              )}

              {/* New comment composer — login olmuş herkes yorum yazabilir.
                  Yorum oluşturma rapor oluşturma yetkisinden bağımsız; Hasura
                  zaten company_id ile satır seviyesinde kısıtlıyor. */}
              {!!user && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'flex-start',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  }}
                >
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: colorForUser(user),
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {userInitials(user)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      maxRows={6}
                      placeholder={t('reportManagement.cardDetail.commentPlaceholder', { defaultValue: 'Yorumunuz...' })}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      disabled={submitting}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          bgcolor: 'background.paper',
                          fontSize: '0.85rem',
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAdd}
                        disabled={submitting || !newComment.trim()}
                        startIcon={submitting ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: '0.95rem' }} />}
                        sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                      >
                        {t('reportManagement.cardDetail.addComment', { defaultValue: 'Yorum Ekle' })}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {t('common.close', { defaultValue: 'Kapat' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardDetailModal;
